const OLIVE = "#757B2B";
const SALMON = "#F3694D";

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const statusClass = (statusKey) => {
  if (statusKey === "LOW") return "status-low";
  if (statusKey === "OUT") return "status-out";
  return "status-ok";
};

export function buildStockReportHtml({
  logoUrl,
  reportDate,
  branchName,
  branchAddress,
  userName,
  filters,
  summary,
  rows,
}) {
  const rowsHtml = rows
    .map(
      (row) => `
        <tr>
          <td>${escapeHtml(row.code)}</td>
          <td>${escapeHtml(row.product)}</td>
          <td>${escapeHtml(row.category)}</td>
          <td>${escapeHtml(row.presentation)}</td>
          <td>${escapeHtml(row.readableStock)}</td>
          <td><span class="status ${statusClass(row.statusKey)}">${escapeHtml(row.statusLabel)}</span></td>
          <td class="money">${escapeHtml(row.cost)}</td>
          <td class="money">${escapeHtml(row.estimatedValue)}</td>
        </tr>
      `
    )
    .join("");

  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Reporte de Existencias</title>
  <style>
    @page {
      size: letter portrait;
      margin: 16mm 12mm 18mm;
    }
    * { box-sizing: border-box; }
    body {
      font-family: Arial, sans-serif;
      color: #1f2933;
      margin: 0;
      font-size: 12px;
    }
    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: 4px solid ${OLIVE};
      padding-bottom: 12px;
      margin-bottom: 16px;
      gap: 18px;
    }
    .brand {
      display: flex;
      align-items: center;
      gap: 12px;
      min-width: 0;
    }
    .logo {
      width: 64px;
      height: 64px;
      object-fit: contain;
      border-radius: 10px;
      border: 1px solid #e5e7eb;
    }
    .title {
      font-size: 22px;
      font-weight: 800;
      color: ${OLIVE};
      margin: 0;
      text-transform: uppercase;
    }
    .subtitle {
      margin: 2px 0 0;
      color: #6b7280;
      font-size: 12px;
    }
    .meta {
      text-align: right;
      font-size: 11px;
      color: #374151;
      line-height: 1.55;
      white-space: nowrap;
    }
    .section {
      margin-top: 14px;
      margin-bottom: 14px;
    }
    .info-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 6px 16px;
      background: #f8fafc;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 10px;
    }
    .label {
      font-weight: 700;
      color: #374151;
    }
    .kpis {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 8px;
      margin: 14px 0;
    }
    .kpi {
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 10px;
      background: #ffffff;
      border-top: 3px solid ${SALMON};
    }
    .kpi-value {
      font-size: 18px;
      font-weight: 800;
      color: ${OLIVE};
    }
    .kpi-label {
      font-size: 10px;
      color: #6b7280;
      margin-top: 2px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 10px;
      page-break-inside: auto;
    }
    thead { display: table-header-group; }
    thead tr {
      background: ${OLIVE};
      color: #ffffff;
    }
    th {
      text-align: left;
      padding: 8px 6px;
      font-size: 10px;
      text-transform: uppercase;
    }
    td {
      padding: 7px 6px;
      border-bottom: 1px solid #e5e7eb;
      vertical-align: top;
      font-size: 10.5px;
    }
    tbody tr:nth-child(even) { background: #f9fafb; }
    tr { page-break-inside: avoid; page-break-after: auto; }
    .money {
      text-align: right;
      white-space: nowrap;
    }
    .status {
      font-weight: 700;
      border-radius: 999px;
      padding: 3px 7px;
      display: inline-block;
      font-size: 9.5px;
      white-space: nowrap;
    }
    .status-ok { background: #dcfce7; color: #166534; }
    .status-low { background: #fef3c7; color: #92400e; }
    .status-out { background: #fee2e2; color: #991b1b; }
    .totals {
      margin-top: 16px;
      display: flex;
      justify-content: flex-end;
      page-break-inside: avoid;
    }
    .total-box {
      width: 280px;
      border: 2px solid ${OLIVE};
      border-radius: 10px;
      padding: 12px;
      background: #f7f8ef;
    }
    .total-label {
      color: #374151;
      font-size: 11px;
      font-weight: 700;
    }
    .total-value {
      color: ${OLIVE};
      font-size: 22px;
      font-weight: 900;
      text-align: right;
      margin-top: 4px;
    }
    .footer {
      margin-top: 18px;
      border-top: 1px solid #e5e7eb;
      padding-top: 8px;
      color: #6b7280;
      font-size: 10px;
      display: flex;
      justify-content: space-between;
    }
    .page-number::after { content: counter(page); }
  </style>
</head>
<body>
  <div class="header">
    <div class="brand">
      ${logoUrl ? `<img class="logo" src="${escapeHtml(logoUrl)}" />` : ""}
      <div>
        <h1 class="title">Reporte de Existencias</h1>
        <p class="subtitle">Control de stock actual por producto</p>
      </div>
    </div>
    <div class="meta">
      <div><strong>Fecha:</strong> ${escapeHtml(reportDate)}</div>
      <div><strong>Sucursal:</strong> ${escapeHtml(branchName)}</div>
      <div><strong>Generado por:</strong> ${escapeHtml(userName)}</div>
    </div>
  </div>

  <div class="section info-grid">
    <div><span class="label">Sucursal:</span> ${escapeHtml(branchName)}</div>
    <div><span class="label">Direccion:</span> ${escapeHtml(branchAddress)}</div>
    <div><span class="label">Filtro busqueda:</span> ${escapeHtml(filters.query)}</div>
    <div><span class="label">Estado:</span> ${escapeHtml(filters.status)}</div>
    <div><span class="label">Categoria:</span> ${escapeHtml(filters.category)}</div>
    <div><span class="label">Total registros:</span> ${escapeHtml(rows.length)}</div>
  </div>

  <div class="kpis">
    <div class="kpi">
      <div class="kpi-value">${escapeHtml(summary.productsWithStock)}</div>
      <div class="kpi-label">Productos con stock</div>
    </div>
    <div class="kpi">
      <div class="kpi-value">${escapeHtml(summary.productsOut)}</div>
      <div class="kpi-label">Productos agotados</div>
    </div>
    <div class="kpi">
      <div class="kpi-value">${escapeHtml(summary.productsLow)}</div>
      <div class="kpi-label">Stock bajo</div>
    </div>
    <div class="kpi">
      <div class="kpi-value">${escapeHtml(summary.estimatedValue)}</div>
      <div class="kpi-label">Valor estimado</div>
    </div>
  </div>

  <table>
    <thead>
      <tr>
        <th>Codigo</th>
        <th>Producto</th>
        <th>Categoria</th>
        <th>Unidad minima/base</th>
        <th>Existencia</th>
        <th>Estado</th>
        <th>Costo</th>
        <th>Valorizado</th>
      </tr>
    </thead>
    <tbody>
      ${rowsHtml || '<tr><td colspan="8">Sin datos para los filtros aplicados.</td></tr>'}
    </tbody>
  </table>

  <div class="totals">
    <div class="total-box">
      <div class="total-label">VALORIZADO GENERAL</div>
      <div class="total-value">${escapeHtml(summary.estimatedValue)}</div>
    </div>
  </div>

  <div class="footer">
    <span>Sistema Punto de Venta</span>
    <span>Pagina <span class="page-number"></span></span>
  </div>
</body>
</html>`;
}

export default buildStockReportHtml;
